/**
 * Advisor Sales Scorecard — one table, one row per advisor, one team-total row
 * that MUST reconcile cent-for-cent with the dashboard.
 *
 * Data sources (identical to /api/metrics/overview so numbers tie out):
 *   /declines      → decline counts, booked, came-back-and-paid buckets
 *   /appointments  → closed-RO $ recovered (via appointments.ro_total + closed_at)
 *   /metrics/overview → team-total sanity check pinned to dashboard totals
 */

import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Printer } from "lucide-react";

function money(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function todayLabel() {
  return new Date().toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const BOOKED_STATUSES = new Set(["scheduled", "closed"]);
const PAID_STATUSES = new Set(["closed", "walked_in"]); // "Came Back & Paid" per dashboard

function inRange(iso, start) {
  if (!iso || !start) return false;
  try { return new Date(iso) >= start; } catch { return false; }
}

export default function AdvisorSalesScorecardPage() {
  const [advisors, setAdvisors] = useState([]);
  const [declines, setDeclines] = useState([]);
  const [appts, setAppts] = useState([]);
  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState(null);
  const isDemo = useMemo(() => (settings?.demo_mode === true) || (overview?.demo_mode === true), [settings, overview]);
  const mtdLabel = isDemo ? "Last 30 Days" : "This Month";
  const ytdLabel = isDemo ? "Last 365 Days" : "Year-to-Date";
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    Promise.all([
      api.get("/advisors"),
      api.get("/declines"),
      api.get("/appointments"),
      api.get("/metrics/overview"),
      api.get("/settings").catch(() => ({ data: null })),
    ])
      .then(([a, d, ap, ov, s]) => {
        setAdvisors(a.data || []);
        setDeclines(d.data || []);
        setAppts(ap.data || []);
        setOverview(ov.data || null);
        setSettings(s.data || null);
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail) || "Could not load scorecard"))
      .finally(() => setBusy(false));
  }, []);

  const { rows, teamRow } = useMemo(() => {
    if (!advisors.length) return { rows: [], teamRow: null };

    const now = new Date();
    // Demo tenants read a rolling 30d / 365d window so a brand-new month on
    // the demo iPad doesn't open with zeros. Must stay in lock-step with the
    // backend window swap in /api/metrics/overview.
    const demo = (settings?.demo_mode === true) || (overview?.demo_mode === true);
    const monthStart = demo
      ? new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      : new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = demo
      ? new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
      : new Date(now.getFullYear(), 0, 1);

    // Exclude non-real "test/demo" advisors from ranking/aggregation.
    const cleanAdvisors = advisors.filter(
      (a) => !/^test\s*demo/i.test(a.name || "") && !/^demo\b/i.test(a.name || "")
    );

    const advDeclines = new Map();
    cleanAdvisors.forEach((a) => advDeclines.set(a.id, []));
    declines.forEach((d) => {
      if (advDeclines.has(d.advisor_id)) advDeclines.get(d.advisor_id).push(d);
    });

    // Per-advisor closed-RO $ recovered — sums ro_total from closed appointments
    // whose closed_at falls in range AND whose decline belongs to the advisor.
    // Uses THE SAME source rows as /api/metrics/overview so the team-total row
    // ties out to the dollar with the dashboard revenue cards.
    const declineToAdvisor = new Map();
    declines.forEach((d) => declineToAdvisor.set(d.id, d.advisor_id || ""));

    const revByAdvisor = new Map(); // advisor_id → { mtd, ytd }
    cleanAdvisors.forEach((a) => revByAdvisor.set(a.id, { mtd: 0, ytd: 0 }));
    appts.forEach((ap) => {
      if (ap.status !== "closed" || !ap.closed_at) return;
      const advId = declineToAdvisor.get(ap.decline_id);
      if (!advId || !revByAdvisor.has(advId)) return;
      const ca = new Date(ap.closed_at);
      const bucket = revByAdvisor.get(advId);
      if (ca >= yearStart) bucket.ytd += ap.ro_total || 0;
      if (ca >= monthStart) bucket.mtd += ap.ro_total || 0;
    });

    const buildRow = (adv) => {
      const mine = advDeclines.get(adv.id) || [];
      const mtd = mine.filter((d) => inRange(d.created_at, monthStart));
      const ytd = mine.filter((d) => inRange(d.created_at, yearStart));
      const rev = revByAdvisor.get(adv.id) || { mtd: 0, ytd: 0 };

      const bookedMtd = mtd.filter((d) => BOOKED_STATUSES.has(d.status)).length;
      const bookedYtd = ytd.filter((d) => BOOKED_STATUSES.has(d.status)).length;
      const paidMtd = mtd.filter((d) => PAID_STATUSES.has(d.status)).length;
      const paidYtd = ytd.filter((d) => PAID_STATUSES.has(d.status)).length;

      return {
        id: adv.id,
        name: adv.name,
        color: adv.color || "#0f2b6b",
        declinesMtd: mtd.length,
        declinesYtd: ytd.length,
        bookedMtd,
        bookedYtd,
        paidMtd,
        paidYtd,
        closeMtd: mtd.length > 0 ? (paidMtd / mtd.length) * 100 : 0,
        closeYtd: ytd.length > 0 ? (paidYtd / ytd.length) * 100 : 0,
        revMtd: rev.mtd,
        revYtd: rev.ytd,
      };
    };

    const built = cleanAdvisors.map(buildRow);
    built.sort((a, b) => (b.revYtd || 0) - (a.revYtd || 0));

    // Team totals — sum every advisor row. Because per-advisor rows use the same
    // decline/appointment aggregation as /api/metrics/overview, the sum here
    // matches the dashboard's overview totals exactly (no rounding drift).
    const t = built.reduce(
      (acc, r) => ({
        declinesMtd: acc.declinesMtd + r.declinesMtd,
        declinesYtd: acc.declinesYtd + r.declinesYtd,
        bookedMtd: acc.bookedMtd + r.bookedMtd,
        bookedYtd: acc.bookedYtd + r.bookedYtd,
        paidMtd: acc.paidMtd + r.paidMtd,
        paidYtd: acc.paidYtd + r.paidYtd,
        revMtd: acc.revMtd + r.revMtd,
        revYtd: acc.revYtd + r.revYtd,
      }),
      { declinesMtd: 0, declinesYtd: 0, bookedMtd: 0, bookedYtd: 0, paidMtd: 0, paidYtd: 0, revMtd: 0, revYtd: 0 }
    );
    const team = {
      ...t,
      closeMtd: t.declinesMtd > 0 ? (t.paidMtd / t.declinesMtd) * 100 : 0,
      closeYtd: t.declinesYtd > 0 ? (t.paidYtd / t.declinesYtd) * 100 : 0,
    };
    return { rows: built, teamRow: team };
  }, [advisors, declines, appts, settings, overview]);

  // Sanity delta between locally-computed team totals and the dashboard's
  // /metrics/overview — surfaces silently in a footnote so a reviewer can
  // eyeball that the two match. Hidden if perfectly aligned.
  const dashboardDelta = useMemo(() => {
    if (!overview || !teamRow) return null;
    return {
      declinesMtd: (overview.total_declines_mtd || 0) - teamRow.declinesMtd,
      declinesYtd: (overview.total_declines_ytd || 0) - teamRow.declinesYtd,
      bookedMtd: (overview.scheduled_mtd || 0) - teamRow.bookedMtd,
      bookedYtd: (overview.scheduled_ytd || 0) - teamRow.bookedYtd,
      paidMtd: (overview.came_back_paid_mtd || 0) - teamRow.paidMtd,
      paidYtd: (overview.came_back_paid_ytd || 0) - teamRow.paidYtd,
      revMtd: Math.round((overview.gross_recovered_month || 0) - teamRow.revMtd),
      revYtd: Math.round((overview.gross_recovered_ytd || 0) - teamRow.revYtd),
    };
  }, [overview, teamRow]);
  const isReconciled = dashboardDelta && Object.values(dashboardDelta).every((v) => v === 0);

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Advisor Sales Scorecard" />

      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 no-print">
        <div className="min-w-0">
          <div className="font-mono-label text-muted-foreground">Recovered Revenue</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-1">
            Advisor Sales Scorecard
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            One row per advisor. Team totals reconcile with the dashboard cent-for-cent.
            Print it. Post it. Hand it to the GM.
          </p>
        </div>
        <div className="flex gap-2 sm:shrink-0">
          <Button
            onClick={() => window.print()}
            data-testid="scorecard-print-btn"
            className="bg-navy hover:bg-navy/90 text-white"
          >
            <Printer className="w-4 h-4 mr-2" strokeWidth={2} />
            Print scorecard
          </Button>
        </div>
      </div>

      {/* Print-only sub-header */}
      <div className="hidden print:block mb-4">
        <div className="text-sm text-neutral-600">
          {settings?.dealership_name || "Your Dealership"} · Prepared {todayLabel()}
        </div>
      </div>

      {busy && (
        <Card className="p-10 text-center text-sm text-muted-foreground border-border" data-testid="scorecard-loading">
          Loading scorecard…
        </Card>
      )}

      {!busy && rows.length === 0 && (
        <Card className="p-10 text-center text-sm text-muted-foreground border-border" data-testid="scorecard-empty">
          No advisor data yet — add advisors or run a Nightly Import.
        </Card>
      )}

      {!busy && rows.length > 0 && teamRow && (
        <Card className="p-0 overflow-hidden border-border" data-testid="scorecard-table-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead rowSpan={2} className="align-bottom">Advisor</TableHead>
                <TableHead colSpan={5} className="text-center border-l border-border">{mtdLabel}</TableHead>
                <TableHead colSpan={5} className="text-center border-l border-border">{ytdLabel}</TableHead>
              </TableRow>
              <TableRow>
                <TableHead className="text-right border-l border-border">Declines</TableHead>
                <TableHead className="text-right">Booked</TableHead>
                <TableHead className="text-right">Came Back &amp; Paid</TableHead>
                <TableHead className="text-right">Close %</TableHead>
                <TableHead className="text-right">Dollars Recovered</TableHead>
                <TableHead className="text-right border-l border-border">Declines</TableHead>
                <TableHead className="text-right">Booked</TableHead>
                <TableHead className="text-right">Came Back &amp; Paid</TableHead>
                <TableHead className="text-right">Close %</TableHead>
                <TableHead className="text-right">Dollars Recovered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-navy/5 dark:bg-navy/20 border-b-2 border-navy/30 font-semibold" data-testid="scorecard-team-total-row">
                <TableCell className="font-display font-black text-navy dark:text-blue-200">TEAM TOTAL</TableCell>
                <TableCell className="text-right tabular-nums border-l border-border" data-testid="team-declines-mtd">{teamRow.declinesMtd}</TableCell>
                <TableCell className="text-right tabular-nums" data-testid="team-booked-mtd">{teamRow.bookedMtd}</TableCell>
                <TableCell className="text-right tabular-nums" data-testid="team-paid-mtd">{teamRow.paidMtd}</TableCell>
                <TableCell className="text-right tabular-nums">{teamRow.closeMtd.toFixed(1)}%</TableCell>
                <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold" data-testid="team-rev-mtd">{money(teamRow.revMtd)}</TableCell>
                <TableCell className="text-right tabular-nums border-l border-border" data-testid="team-declines-ytd">{teamRow.declinesYtd}</TableCell>
                <TableCell className="text-right tabular-nums" data-testid="team-booked-ytd">{teamRow.bookedYtd}</TableCell>
                <TableCell className="text-right tabular-nums" data-testid="team-paid-ytd">{teamRow.paidYtd}</TableCell>
                <TableCell className="text-right tabular-nums">{teamRow.closeYtd.toFixed(1)}%</TableCell>
                <TableCell className="text-right tabular-nums text-navy dark:text-blue-200 font-bold" data-testid="team-rev-ytd">{money(teamRow.revYtd)}</TableCell>
              </TableRow>
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`scorecard-row-${r.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span className="w-2.5 h-2.5 rounded-full ring-2 ring-background shadow-sm shrink-0" style={{ backgroundColor: r.color }} />
                      <span className="font-medium" style={{ color: r.color }}>{r.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums border-l border-border">{r.declinesMtd}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.bookedMtd}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.paidMtd}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.closeMtd.toFixed(1)}%</TableCell>
                  <TableCell className="text-right tabular-nums text-navy dark:text-blue-200">{money(r.revMtd)}</TableCell>
                  <TableCell className="text-right tabular-nums border-l border-border">{r.declinesYtd}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.bookedYtd}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.paidYtd}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.closeYtd.toFixed(1)}%</TableCell>
                  <TableCell className="text-right tabular-nums text-navy dark:text-blue-200">{money(r.revYtd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Reconciliation banner — reads the dashboard's own overview endpoint
              and compares against the sum of the per-advisor rows. Any drift is
              flagged so a reviewer knows the two views disagree. */}
          {overview && (
            <div
              className={`px-5 py-3 border-t border-border text-xs font-mono-label tracking-wide ${
                isReconciled ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200" : "bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
              }`}
              data-testid="scorecard-reconciliation"
            >
              {isReconciled ? (
                <>✓ Team totals match dashboard exactly · Recovered {money(overview.gross_recovered_month || 0)} MTD · {money(overview.gross_recovered_ytd || 0)} YTD</>
              ) : (
                <>Δ from dashboard — declines {dashboardDelta.declinesMtd}/{dashboardDelta.declinesYtd}, booked {dashboardDelta.bookedMtd}/{dashboardDelta.bookedYtd}, paid {dashboardDelta.paidMtd}/{dashboardDelta.paidYtd}, $ {dashboardDelta.revMtd}/{dashboardDelta.revYtd}</>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Print footer */}
      <div className="hidden print:block mt-6 text-[0.65rem] text-neutral-500 leading-relaxed">
        Recovered revenue = closed RO totals invoiced against declined-service SMS coupons.
        Attribution is per writing advisor at time of decline. Confidential — for
        dealership internal use only. Prepared by Decline Drive™ ·
        Ayers Enterprises.
      </div>
    </DashboardShell>
  );
}
